"use client"

import React, { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Sparkles, ArrowRight } from "lucide-react"

interface SummaryItem {
    name: string
    quantity: number
}

interface PaymentSuccessModalProps {
    isOpen: boolean
    onClose: () => void
    summary: SummaryItem[]
}

const ConfettiParticle = ({ color, delay }: { color: string; delay: number }) => {
    const angle = Math.random() * Math.PI * 2
    const velocity = 200 + Math.random() * 200
    const duration = 1.5 + Math.random() * 1

    return (
        <motion.div
            initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
            animate={{
                x: Math.cos(angle) * velocity,
                y: Math.sin(angle) * velocity + 100, // gravity effect
                scale: [0, 1, 0.5, 0],
                opacity: [1, 1, 0],
                rotate: Math.random() * 360,
            }}
            transition={{
                duration,
                delay,
                ease: "easeOut",
            }}
            className="absolute w-2 h-2 rounded-sm"
            style={{ backgroundColor: color }}
        />
    )
}

export function PaymentSuccessModal({ isOpen, onClose, summary }: PaymentSuccessModalProps) {
    const [particles, setParticles] = useState<{ id: number; color: string; delay: number }[]>([])

    useEffect(() => {
        if (isOpen) {
            const colors = ["#4F46E5", "#10B981", "#FBBF24", "#EF4444", "#8B5CF6"]
            const newParticles = Array.from({ length: 50 }).map((_, i) => ({
                id: i,
                color: colors[i % colors.length],
                delay: Math.random() * 0.2,
            }))
            setParticles(newParticles)
        } else {
            setParticles([])
        }
    }, [isOpen])

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    {/* Overlay */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm shadow-xl"
                    />

                    {/* Modal Content */}
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 20 }}
                        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden text-center"
                    >
                        {/* Confetti Container */}
                        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                            {particles.map((p) => (
                                <ConfettiParticle key={p.id} color={p.color} delay={p.delay} />
                            ))}
                        </div>

                        {/* Header */}
                        <div className="bg-gradient-to-br from-emerald-500 to-teal-500 px-8 pt-10 pb-8 flex flex-col items-center text-center relative z-10">
                            {/* Checkmark Animation */}
                            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mb-6 shadow-lg">
                                <motion.svg
                                    width="32"
                                    height="32"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="white"
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <motion.path
                                        d="M4 12L9 17L20 6"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 0.5, delay: 0.5 }}
                                    />
                                </motion.svg>
                            </div>
                            <h2 className="text-white font-bold text-[22px] leading-tight mb-1">Payment Successful!</h2>
                            <p className="text-white/80 text-[13px]">Your decks have been added to your account.</p>
                        </div>

                        {/* Decks Summary */}
                        <div className="px-8 py-6 relative z-10">
                            <div className="flex items-center gap-2 mb-4 text-left">
                                <Sparkles className="w-4 h-4 text-amber-500" />
                                <span className="text-[13px] font-bold text-slate-700">Decks Added</span>
                            </div>
                            <div className="space-y-2.5 max-h-48 overflow-y-auto">
                                {summary.map((item) => (
                                    <div key={item.name} className="flex items-center justify-between bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-2.5">
                                        <span className="text-[13px] font-semibold text-slate-700">{item.name}</span>
                                        <span className="text-[13px] font-bold text-emerald-600">+{item.quantity} {item.quantity > 1 ? 'decks' : 'deck'}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Action */}
                        <div className="px-8 pb-8 relative z-10">
                            <button
                                onClick={onClose}
                                className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-700 text-white font-bold text-[14px] py-3 rounded-lg transition-all cursor-pointer shadow-lg active:scale-[0.98]"
                            >
                                Start Creating
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    )
}
